import {useQuery} from "@tanstack/react-query";

import {eatClubClient} from "@globals/config/apiClient";
import {DateString, ResponseError} from "@globals/types/utilityTypes";

type MenuDatesQueryParams = {
  hubShortName?: string;
  tenantSlug?: string;
  includeToday?: boolean;
  locationId?: number;
  disabled?: boolean;
};

export type HolidayDetailsItem = {
  id: number;
  date: DateString;
  name: string;
  note: string;
  operator: number;
};

export type HolidayDetails = {
  [key: DateString]: HolidayDetailsItem;
};

type MenuDates = {
  closedDates: DateString[];
  menuDates: DateString[];
  holidayDetails: HolidayDetails;
};

type MenuDatesResource = MenuDates & {
  isLoading: boolean;
  isError: boolean;
  error: ResponseError;
  isSuccess: boolean;
};

export function fetchMenuDates(
  params: MenuDatesQueryParams = {}
): Promise<MenuDates> {
  return eatClubClient
    .get("/api/v3/menu-dates/", {
      params: {
        hub_short_name: params.hubShortName,
        tenant_slug: params.tenantSlug,
        include_today: params.includeToday ?? false,
        location_id: params.locationId,
      },
    })
    .then((response) => {
      const {
        menu_dates: menuDates,
        closed_dates: closedDatesResp,
        holidays,
        holiday_details: holidayDetails,
      } = response.data;

      const closedDates = menuDates.filter(
        (date: string) =>
          holidays.includes(date) || closedDatesResp.includes(date)
      );

      return {
        menuDates,
        closedDates,
        holidayDetails,
      };
    })
    .catch(({response}) => {
      const error = {
        status: response.status,
        message: response.data?.message,
      };
      throw error;
    });
}

export const MENU_DATES_QUERY = "menu-dates";

export default function useMenuDates(
  params: MenuDatesQueryParams
): MenuDatesResource {
  const {data, isError, isPending, error, isSuccess} = useQuery<
    MenuDates,
    ResponseError
  >({
    queryKey: [
      MENU_DATES_QUERY,
      params.hubShortName,
      params.tenantSlug,
      params.includeToday,
      params.locationId,
    ],
    queryFn: () => fetchMenuDates(params),
    enabled: !params.disabled,
  });

  return {
    ...data,
    menuDates: data?.menuDates || [],
    closedDates: data?.closedDates || [],
    holidayDetails: data?.holidayDetails || {},
    isError,
    isLoading: isPending,
    error,
    isSuccess,
  };
}
